import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import moment from 'moment-timezone';
import { Subscription, filter } from 'rxjs';

import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { DateInputTypesEnum } from '../../../Forms/enums/date-input-types.enum';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { TimeModel } from '../../../Forms/models/time.model';
import { TwentyFourHourModel } from '../../../Forms/models/twenty-four-hour.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { MinuteModel } from '../../../Forms/models/minute.model';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { meridiansEnum } from '../../enums/meridians.enum';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { DateQueryDateTypeEnum } from '../../../../api/mentions/enum/date-query-date-type.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';


export const defaultDateTypeOptions: DropdownOptionModel[] = [
    new DropdownOptionModel(
        'Monitoring.result.dateFilter.option.published',
        DateQueryDateTypeEnum.PUBLISHED
    ),
    new DropdownOptionModel(
        'Monitoring.result.dateFilter.option.harvested',
        DateQueryDateTypeEnum.HARVESTED
    ),
];
export const savedSourceDateTypeOptions: DropdownOptionModel[] = [
    new DropdownOptionModel(
        'Monitoring.result.dateFilter.option.published',
        DateQueryDateTypeEnum.PUBLISHED
    ),
    new DropdownOptionModel(
        'Monitoring.result.dateFilter.option.harvested',
        DateQueryDateTypeEnum.HARVESTED
    ),
    new DropdownOptionModel(
        'Monitoring.result.dateFilter.option.saved',
        DateQueryDateTypeEnum.SAVED
    ),
    new DropdownOptionModel(
        'Monitoring.result.dateFilter.option.edited',
        DateQueryDateTypeEnum.EDITED
    )
];

@Component({
    selector: 'agpr-date-range-selector',
    templateUrl: './date-range-selector.component.html',
    styleUrls: ['./date-range-selector.component.scss'],
    providers: [getProvider(DateRangeSelectorComponent)],
    standalone: false
})
export class DateRangeSelectorComponent extends ControlValueAccessorModel<DropdownOptionModel> implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() header: string;
    @Input() options: DropdownOptionModel[];
    @Input() dateRange: SearchRangeModel;
    @Input() useToDate: boolean = false;
    @Input() dateTypeOptions: DropdownOptionModel[] = defaultDateTypeOptions;
    @Input() hideDateTypeSelection: boolean = false;
    @Input() inputPlaceholder: string = '';
    @Input() setEndOfDayTime: boolean = false;

    @ViewChild('calendarStart') calendarStart: any;
    @ViewChild('calendarEnd') calendarEnd: any;

    selectedDateTypeOption: DropdownOptionModel;
    selectedDateRangeOption: DropdownOptionModel;

    calendarStartValue: Date;
    calendarEndValue: Date;

    startTime: TimeModel;
    endTime: TimeModel;

    dateRangeErrorMessage: string;

    private subscriptions: Subscription[] = [];
    private preferences: PreferencesModel;

    readonly dateQueryTypeEnum = DateQueryTypeEnum;
    readonly dateQueryDateTypeEnum = DateQueryDateTypeEnum;

    constructor(
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.preferences = preferences;

                    this.updateDates(true);
                }
            )
        );
        this.dateTypeOptions.forEach(option => option.label = this.localizationService.transform(option.label));
    }

    writeValue(value: DropdownOptionModel): void {
        this.value = value;
        if (this.value && this.dateTypeOptions?.length) {
            this.selectedDateTypeOption = this.dateTypeOptions.find((option) => {
                return option.value === this.value.value.query.dateType;
            });
            this.selectedDateTypeOption = this.selectedDateTypeOption ? this.selectedDateTypeOption : this.dateTypeOptions[0];
        }

        if (this.value && this.options?.length) {
            this.selectedDateRangeOption = this.options.find((option) => {
                return option.label === this.value.label;
            });
            this.selectedDateRangeOption = this.selectedDateRangeOption ? this.selectedDateRangeOption : this.options[0];
        }

        this.updateDates();
    }

    /**
     * Sets the date and time fields based on the selected date range option.
     */
    updateDates(resetCustomOption: boolean = false): void {
        if (!this.value) {
            return;
        }
        const type: DateQueryTypeEnum = this.value.value.query.type;
        const query: DateQueryModel = this.value.value.query;
        let startDate;
        let endDate;

        switch (type) {
            case DateQueryTypeEnum.ALL:
                this.calendarStartValue = null;
                this.calendarEndValue = null;
                this.startTime = new TimeModel(this.preferences.timeFormat);
                this.endTime = new TimeModel(this.preferences.timeFormat);
                break;

            case DateQueryTypeEnum.RELATIVE:
                startDate = moment().tz(this.preferences.timeZone).add(query.relativeStartOffsetH, 'hours');
                endDate = moment().tz(this.preferences.timeZone).add(query.relativeEndOffsetH, 'hours');
                this.setDatesAndTimes(startDate, endDate);
                break;
            case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                startDate = DateQueryModel.getStartDateForRelativeTimezone(query.relativeTimezoneDateType, this.preferences.timeZone);
                endDate = DateQueryModel.getEndDateForRelativeTimezone(query.relativeTimezoneDateType, this.preferences.timeZone);
                this.setDatesAndTimes(startDate, endDate);
                break;
            case DateQueryTypeEnum.ABSOLUTE:
                // Use values on the custom option if they are already set.
                if (query.absoluteRange) {
                    startDate = moment(query.absoluteRange.start).tz(this.preferences.timeZone);
                    endDate = moment(query.absoluteRange.end).tz(this.preferences.timeZone);

                    this.setDatesAndTimes(startDate, endDate);
                } else {
                    // set the date to be applied. note that in this case, time will be overwritten
                    // later to set the actual range to 'beginning of yesterday (midnight) -> end of today'
                    // which means the range here is actually greater than 24 hours on average
                    // so the job here is mostly just to ensure the startDate is 'yesterday'
                    // using something like moment().startOf or endOf() doesn't really work because of
                    // complications with the utc offsets we apply in the function setDatesAndTimes
                    startDate = moment().add(-24, 'hours');
                    endDate = moment();
                    this.setDatesAndTimes(startDate, endDate, true, true);

                    // if we would open up the dialog & just click apply, it would fail because we never set the value.absoluteRange, so we have to apply it.
                    this.apply();
                }

                break;
        }

        // If the selected date range is not the custom one, reset it to the default;
        if (resetCustomOption && type !== DateQueryTypeEnum.ABSOLUTE) {
            delete this.options.find(
                (option) => {
                    return option.value.type === DateInputTypesEnum.custom;
                }
            ).value.query.absoluteRange;
        }
    }

    startOrEndTimeChanged(): void {
        this.switchToCustomRange(true);
    }

    /**
     * Sets the date range fields based on the start and end dates.
     */
    setDatesAndTimes(startDate, endDate, fillEmptyOnly: boolean = false, doResetTime = false): void {
        // Need to apply an offset on the date because moment's toDate() function converts the date back to browser time.
        const offset: number = moment().tz(this.preferences.timeZone).utcOffset() - moment().utcOffset();
        if (!fillEmptyOnly || !this.calendarStartValue) {
            this.calendarStartValue = moment(startDate.toDate()).add(offset, 'minutes').toDate();
        }

        if (!fillEmptyOnly || !this.calendarEndValue) {
            this.calendarEndValue = moment(endDate.toDate()).add(offset, 'minutes').toDate();
        }

        if (!fillEmptyOnly || !this.startTime) {
            this.startTime = new TimeModel(timeFormatsEnum.twentyFourHour);
        }

        if (!fillEmptyOnly || (!this.startTime.hour && this.startTime.hour !== 0)) {
            this.startTime.hour = startDate.get('hour') as TwentyFourHourModel;
            this.startTime.format = timeFormatsEnum.twentyFourHour;
        }

        if (!fillEmptyOnly || (!this.startTime.minute && this.startTime.minute !== 0)) {
            this.startTime.minute = startDate.get('minute') as MinuteModel;
        }

        if (!fillEmptyOnly || !this.endTime) {
            this.endTime = new TimeModel(timeFormatsEnum.twentyFourHour);
        }

        if (!fillEmptyOnly || (!this.endTime.hour && this.endTime.hour !== 0)) {
            this.endTime.hour = endDate.get('hour') as TwentyFourHourModel;
            this.endTime.format = timeFormatsEnum.twentyFourHour;
        }

        if (!fillEmptyOnly || (!this.endTime.minute && this.endTime.minute !== 0)) {
            this.endTime.minute = endDate.get('minute') as MinuteModel;
        }

        // if we're setting dates and times after switching to custom mode without having any time
        // values set by the user prior, they should default to start: midnight, end: 11:59:59pm.
        if (doResetTime) {
            this.startTime = new TimeModel(timeFormatsEnum.twentyFourHour, 0, 0, meridiansEnum.AM, null, 0);
            if (this.setEndOfDayTime) {
                this.endTime = new TimeModel(timeFormatsEnum.twentyFourHour, 23, 59, meridiansEnum.PM, null, 59);
            }
        }

        this.startTime = this.startTime.changeFormat(this.preferences.timeFormat);
        this.endTime = this.endTime.changeFormat(this.preferences.timeFormat);
    }

    /**
     * Fires when the user selects a date range
     */
    dateRangeSelected(option: DropdownOptionModel): void {
        this.value = option;
        this.selectedDateRangeOption = option;
        this.updateDates();
        this.apply();
    }

    /**
     * Switches the radio button selection to the custom option. Used for when the user edits the custom range fields.
     */
    switchToCustomRange(timeModified = false): void {
        let doResetTime: boolean = false;
        if (!timeModified && this.value.value.query.type !== DateQueryTypeEnum.ABSOLUTE) {
            // we're switching to custom mode from another mode and we modified more than just the
            // time values: we want to reset the time values to their defaults
            doResetTime = true;
        }

        // The user modified the custom dates, so switch to the custom date option.
        this.dateRangeErrorMessage = null;
        this.value = this.options.find(
            (option) => {
                return option.value.type === DateInputTypesEnum.custom;
            }
        );

        this.selectedDateRangeOption = this.value;
        const startDate = moment().add(-24, 'hours');
        const endDate = moment();
        this.setDatesAndTimes(startDate, endDate, true, doResetTime);
        this.apply();
    }

    openCalendar(event, calendar): void {
        calendar.hideOverlay();
        calendar.showOverlay();
        event.stopPropagation();
    }

    /**
     * Covert and emit the value of value
     */
    apply(): void {
        if (this.value.value.type === DateInputTypesEnum.custom) {
            if (!this.calendarStartValue || !this.calendarEndValue
                || !this.startTime || !this.endTime
            ) {
                this.dateRangeErrorMessage = 'Monitoring.dateQueryDialog.error.invalidDateRange';
                return;
            }

            const startTime24Hour: TimeModel = this.startTime.changeFormat(timeFormatsEnum.twentyFourHour);
            const endTime24Hour: TimeModel = this.endTime.changeFormat(timeFormatsEnum.twentyFourHour);

            this.calendarStartValue.setHours(startTime24Hour.hour);
            this.calendarStartValue.setMinutes(startTime24Hour.minute);
            this.calendarEndValue.setHours(endTime24Hour.hour);
            this.calendarEndValue.setMinutes(endTime24Hour.minute);

            if (this.calendarStartValue > this.calendarEndValue) {
                this.dateRangeErrorMessage = 'Monitoring.dateQueryDialog.error.invalidDateRange';
                return;
            }

            this.value.value.query = DateQueryModel.getAbsoluteQuery();
            this.value.value.query.absoluteRange = {
                // Sets the timezone to the user preference so the .toDate() function gets the correct value in the browser timezone.
                start: moment(this.calendarStartValue).tz(this.preferences.timeZone, true).startOf('minute').toDate(),
                end: moment(this.calendarEndValue).tz(this.preferences.timeZone, true).endOf('minute').toDate()
            };
        }
        if (this.selectedDateTypeOption) {
            this.value.value.query.dateType = this.selectedDateTypeOption.value;
        } else {
            this.value.value.query.dateType = null;
        }

        this.onModelChange(this.value);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
